import { Component, OnInit } from '@angular/core';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FormBuilder, FormControl, FormGroup, FormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { ToastModule } from 'primeng/toast';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../core/services/auth';
import { MessageService } from 'primeng/api';
import { SETTINGS } from '../../core/config/common.settings';

@Component({
  selector: 'app-reset-password',
  imports: [InputGroupModule, InputGroupAddonModule, FormsModule, InputTextModule, CommonModule, ReactiveFormsModule, ButtonModule, PasswordModule, ToastModule],
  providers: [MessageService],
  templateUrl: './reset-password.html',
  styleUrl: './reset-password.scss'
})
export class ResetPassword implements OnInit {
  resetPasswordForm: FormGroup;
  isLoading: boolean = false;
  authError: string = "";
  userToken: string | null = null;
  userType: string = "";
  validating = true;

  private readonly errorMessages: { [key: string]: string } = {
    '0 unknown error': 'Unable to connect to the server. Please check your internet connection and try again.',
    'network error': 'Network error. Please check your internet connection and try again.',
    'timeout': 'Request timed out. Please try again.',
  };

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService
  ) {
    this.resetPasswordForm = this.fb.group({
      newPassword: [
        "",
        [
          Validators.required,
          Validators.minLength(SETTINGS.PASSWORD.MIN_LENGTH),
          Validators.pattern(SETTINGS.PASSWORD.VALIDATION_REGEX)
        ],
      ],
      confirmPassword: [
        "",
        [Validators.required, this.passwordMatchValidator.bind(this)],
      ],
    });
  }

  ngOnInit(): void {
    this.userToken = this.route.snapshot.paramMap.get("token");
  }

  passwordMatchValidator(
    control: FormControl
  ): { [s: string]: boolean } | null {
    if (!control.value) {
      return { required: true };
    } else if (
      control.value !== this.resetPasswordForm.controls["newPassword"].value
    ) {
      return { mismatch: true };
    }
    return null;
  }

  getPasswordError() {
    const control = this.resetPasswordForm.get("newPassword");
    if (control?.hasError("required")) return SETTINGS.PASSWORD_ERROR_MESSAGES.REQUIRED;
    if (control?.hasError("pattern"))
      return SETTINGS.PASSWORD_ERROR_MESSAGES.INVALID_FORMAT;
    if (control?.hasError("minLength"))
      return `Password must be at least ${SETTINGS.PASSWORD.MIN_LENGTH} characters`;
    return "";
  }

  getConfirmPasswordError() {
    const control = this.resetPasswordForm.get("confirmPassword");
    if (control?.invalid && control?.touched) {
      if (control.hasError("required")) return "Confirm Password is required.";
      if (control.hasError("mismatch")) return "Passwords do not match.";
    }
    return "";
  }

  // Get password strength
  getPasswordStrength(password: string): { score: number; label: string; color: string } {
    if (!password) return { score: 0, label: 'Enter password', color: 'bg-gray-300' };
    
    let score = 0;
    if (password.length >= SETTINGS.PASSWORD.MIN_LENGTH) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[\W_]/.test(password)) score++;
    
    if (score <= 2) return { score, label: 'Weak', color: 'bg-red-500' };
    if (score <= 3) return { score, label: 'Fair', color: 'bg-yellow-500' };
    if (score <= 4) return { score, label: 'Good', color: 'bg-blue-500' };
    return { score, label: 'Strong', color: 'bg-green-500' };
  }

  // Get current password strength
  getCurrentPasswordStrength() {
    const newPassword = this.resetPasswordForm.get('newPassword')?.value;
    return this.getPasswordStrength(newPassword);
  }

  async validateToken (): Promise <void> {
    try{
      this.authService.validatePWResetToken(this.userToken).subscribe(
        (response:any) => {
          if (!response.done) {
            this.showMessage('error', 'Error', 'Token is invalid or expired!');
            this.validating = false;
          }
        },
        (error: any) => {
          this.handleError(error);
          this.validating = false;
        }
      )
    } catch (error) {
      console.error('Error validating token:', error);
      this.showMessage('error', 'Error', 'Failed to validate token');
      this.validating = false;
    }
  }

  private handleError(error: any) {
    const errorMessage = this.extractErrorMessage(error);
    this.showMessage('error', 'Error', errorMessage);
  }

  private extractErrorMessage(error: any): string {
    const message = error.error?.message || error.message || 'Password reset failed. Please try again.';
    const lowerMessage = message.toLowerCase();
    
    // Handle server down/network errors specifically
    if (error.status === 0 || lowerMessage.includes('0 unknown error')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    
    if (error.status >= 500) {
      return 'Server error. Please try again later.';
    }
    
    // Check for specific error patterns
    for (const [pattern, userMessage] of Object.entries(this.errorMessages)) {
      if (lowerMessage.includes(pattern)) {
        return userMessage;
      }
    }
    
    return message;
  }

  private showMessage(severity: string, summary: string, detail: string) {
    this.messageService.add({ severity, summary, detail });
  }

  onSubmit() {
    if (this.resetPasswordForm.invalid) {
      Object.keys(this.resetPasswordForm.controls).forEach((field) => {
        const control = this.resetPasswordForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      return;
    }

    this.isLoading = true;
    const formData = this.resetPasswordForm.getRawValue();
    const payload = {
      token: this.userToken,
      password: formData.newPassword,
    };

    this.authService.resetPassword(payload).subscribe(
      (response:any) => {
       if (response.done) {
        this.showMessage('success', 'Success', 'Password reset successfully');
        setTimeout(() => {
          this.router.navigate(['/']);
        }, 1000);
       }
       this.isLoading = false;
      }, 
      (error:any) => {
        this.handleError(error);
        this.isLoading = false;
      }
    )
  }
}
